import React, { useState, useEffect, useCallback } from 'react';
import { createClientComponentClient } from '@/integrations/supabase/client';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  Image as ImageIcon, Video, Search, Filter, Trash2, Edit3,
  ExternalLink, CheckCircle2, XCircle, Sparkles, RefreshCw,
  Plus, X, AlertTriangle, Eye
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export interface LessonMediaRecord {
  id: string;
  class_id: string | null;
  lesson_unit: number;
  lesson_number: number;
  media_type: 'image' | 'video' | string;
  url: string;
  caption: string | null;
  attribution: string | null;
  source: string | null;
  approved: boolean | null;
  created_at?: string | null;
  class_name?: string;
}

export const LessonMediaManagement: React.FC = () => {
  const [mediaList, setMediaList] = useState<LessonMediaRecord[]>([]);
  const [classes, setClasses] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [classFilter, setClassFilter] = useState<string>('all');

  // Modal states
  const [previewItem, setPreviewItem] = useState<LessonMediaRecord | null>(null);
  const [editItem, setEditItem] = useState<LessonMediaRecord | null>(null);
  const [editCaption, setEditCaption] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);

  // New item form
  const [newClassId, setNewClassId] = useState('');
  const [newUnit, setNewUnit] = useState(1);
  const [newLesson, setNewLesson] = useState(1);
  const [newType, setNewType] = useState<'image' | 'video'>('image');
  const [newUrl, setNewUrl] = useState('');
  const [newCaption, setNewCaption] = useState('');
  const [newAttribution, setNewAttribution] = useState('');
  const [newSource, setNewSource] = useState('custom');

  const supabase = createClientComponentClient();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch classes for name resolution
      const { data: classData } = await supabase
        .from('classes')
        .select('id, name')
        .order('name');
      const classMap = new Map((classData || []).map(c => [c.id, c.name]));
      setClasses(classData || []);

      // 2. Fetch all lesson_media
      const { data: mediaData, error: mErr } = await supabase
        .from('lesson_media')
        .select('*')
        .order('created_at', { ascending: false });

      if (mErr) throw mErr;

      const enriched: LessonMediaRecord[] = (mediaData || []).map(item => ({
        ...item,
        class_name: item.class_id ? (classMap.get(item.class_id) || 'Unknown Class') : 'General Curriculum'
      }));

      setMediaList(enriched);
    } catch (err) {
      console.error('Error loading lesson media in admin:', err);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenEdit = (item: LessonMediaRecord) => {
    setEditItem(item);
    setEditCaption(item.caption || '');
    setEditUrl(item.url || '');
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('lesson_media')
        .update({
          caption: editCaption,
          url: editUrl,
        })
        .eq('id', editItem.id);

      if (error) throw error;

      setMediaList(prev => prev.map(m => m.id === editItem.id ? { ...m, caption: editCaption, url: editUrl } : m));
      setEditItem(null);
    } catch (err: any) {
      alert(err.message || 'Failed to update visual');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleApproved = async (item: LessonMediaRecord) => {
    const updatedApproved = !item.approved;
    try {
      const { error } = await supabase
        .from('lesson_media')
        .update({ approved: updatedApproved })
        .eq('id', item.id);

      if (error) throw error;

      setMediaList(prev => prev.map(m => m.id === item.id ? { ...m, approved: updatedApproved } : m));
    } catch (err: any) {
      alert(err.message || 'Failed to toggle approval status');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this visual from the lesson database?')) return;
    try {
      const { error } = await supabase
        .from('lesson_media')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setMediaList(prev => prev.filter(m => m.id !== id));
    } catch (err: any) {
      alert(err.message || 'Failed to delete visual');
    }
  };

  const handleCreateMedia = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUrl) return;
    setIsSaving(true);
    try {
      const item = {
        class_id: newClassId || null,
        lesson_unit: Number(newUnit),
        lesson_number: Number(newLesson),
        media_type: newType,
        url: newUrl,
        caption: newCaption,
        attribution: newAttribution || 'Platform Admin',
        source: newSource || 'admin',
        approved: true,
      };

      const { data, error } = await supabase
        .from('lesson_media')
        .insert([item])
        .select('*')
        .single();

      if (error) throw error;

      const classMap = new Map(classes.map(c => [c.id, c.name]));
      const enriched: LessonMediaRecord = {
        ...data,
        class_name: data.class_id ? (classMap.get(data.class_id) || 'Unknown Class') : 'General Curriculum'
      };

      setMediaList(prev => [enriched, ...prev]);
      setIsAddOpen(false);
      setNewUrl('');
      setNewCaption('');
      setNewAttribution('');
    } catch (err: any) {
      alert(err.message || 'Failed to add lesson media');
    } finally {
      setIsSaving(false);
    }
  };

  // Filter items
  const filteredItems = mediaList.filter(item => {
    const matchesSearch =
      (item.caption || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.class_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.attribution || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.url || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesSource = sourceFilter === 'all' || item.source === sourceFilter;
    const matchesType = typeFilter === 'all' || item.media_type === typeFilter;
    const matchesClass = classFilter === 'all' || item.class_id === classFilter;

    return matchesSearch && matchesSource && matchesType && matchesClass;
  });

  const unsplashCount = mediaList.filter(m => m.source === 'unsplash').length;
  const wikimediaCount = mediaList.filter(m => m.source === 'wikimedia').length;
  const youtubeCount = mediaList.filter(m => m.source === 'youtube').length;

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h2 className="text-3xl font-black text-white flex items-center gap-3">
            <ImageIcon className="w-8 h-8 text-blue-600" />
            Lesson Visuals & AI Vision Hub
          </h2>
          <p className="text-slate-400 font-medium mt-1">
            Browse, inspect, edit, or delete visuals verified by AI Vision (<span className="text-blue-600 font-bold">gpt-5.6-luna</span>) and curriculum archives.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={loadData}
            className="flex items-center gap-2 border-slate-200"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => setIsAddOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add Visual
          </Button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-5 border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            <span className="text-xs font-black uppercase tracking-wider text-slate-400">Total Visuals</span>
            <ImageIcon className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-2xl font-black text-white mt-2">{mediaList.length}</p>
          <p className="text-xs text-slate-400 mt-1">Across all courses</p>
        </Card>

        <Card className="p-5 border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            <span className="text-xs font-black uppercase tracking-wider text-slate-400">AI Curated (Unsplash)</span>
            <Sparkles className="w-5 h-5 text-amber-500" />
          </div>
          <p className="text-2xl font-black text-white mt-2">{unsplashCount}</p>
          <p className="text-xs text-amber-600 font-semibold mt-1">Vision-verified</p>
        </Card>

        <Card className="p-5 border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            <span className="text-xs font-black uppercase tracking-wider text-slate-400">Wikimedia Commons</span>
            <ImageIcon className="w-5 h-5 text-emerald-500" />
          </div>
          <p className="text-2xl font-black text-white mt-2">{wikimediaCount}</p>
          <p className="text-xs text-slate-400 mt-1">Seeded curriculum archive</p>
        </Card>

        <Card className="p-5 border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            <span className="text-xs font-black uppercase tracking-wider text-slate-400">YouTube Embeds</span>
            <Video className="w-5 h-5 text-red-500" />
          </div>
          <p className="text-2xl font-black text-white mt-2">{youtubeCount}</p>
          <p className="text-xs text-slate-400 mt-1">Video lesson resources</p>
        </Card>
      </div>

      {/* Filter Controls */}
      <Card className="p-4 border-slate-200 bg-white space-y-4">
        <div className="flex flex-col md:flex-row gap-3 items-center">
          <div className="relative flex-1 w-full">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by lesson, caption, attribution, or subject..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-9 bg-slate-50 border-slate-200 w-full"
            />
          </div>

          <div className="flex gap-2 w-full md:w-auto flex-wrap">
            <select
              value={classFilter}
              onChange={e => setClassFilter(e.target.value)}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-white"
            >
              <option value="all">All Classes & Subjects</option>
              {classes.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>

            <select
              value={sourceFilter}
              onChange={e => setSourceFilter(e.target.value)}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-white"
            >
              <option value="all">All Sources</option>
              <option value="unsplash">Unsplash (AI Vision)</option>
              <option value="wikimedia">Wikimedia Commons</option>
              <option value="youtube">YouTube</option>
              <option value="admin">Custom / Admin</option>
            </select>

            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-white"
            >
              <option value="all">All Media Types</option>
              <option value="image">Images</option>
              <option value="video">Videos</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Media Cards Grid */}
      {loading ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-slate-200">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
          <p className="text-slate-400 font-bold">Loading lesson visual media archive...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-300">
          <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-800 font-black text-lg">No visuals match your search criteria</p>
          <p className="text-slate-400 text-sm mt-1">Try resetting your filters or search term.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map(item => (
            <Card key={item.id} className="overflow-hidden border-slate-200 bg-white flex flex-col justify-between hover:shadow-lg transition-all duration-300">
              {/* Media Thumbnail */}
              <div className="relative h-48 w-full bg-slate-900 flex items-center justify-center overflow-hidden group">
                {item.media_type === 'image' ? (
                  <img
                    src={item.url}
                    alt={item.caption || 'Lesson visual'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    onError={(e: any) => {
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1532012197267-da84d127e765?w=800';
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center text-white/70 gap-2">
                    <Video className="w-10 h-10 text-red-500" />
                    <span className="text-xs font-bold">Video Embed</span>
                  </div>
                )}

                {/* Overlaid Badges */}
                <div className="absolute top-2 left-2 flex gap-1.5 flex-wrap">
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-900/80 backdrop-blur-md text-white border border-white/10">
                    Unit {item.lesson_unit} · Lesson {item.lesson_number}
                  </span>
                  {item.source === 'unsplash' && (
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/90 text-white flex items-center gap-1 shadow-sm">
                      <Sparkles className="w-3 h-3" /> AI Vision
                    </span>
                  )}
                </div>

                <button
                  onClick={() => setPreviewItem(item)}
                  className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white font-bold transition-opacity"
                >
                  <Eye className="w-6 h-6 mr-2" /> Inspect
                </button>
              </div>

              {/* Media Info */}
              <div className="p-4 flex-1 flex flex-col justify-between space-y-3">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-xs font-black text-blue-600 truncate">
                      {item.class_name}
                    </span>
                    <button
                      onClick={() => handleToggleApproved(item)}
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${
                        item.approved
                          ? 'bg-emerald-100 text-emerald-300'
                          : 'bg-rose-100 text-rose-700'
                      }`}
                      title="Click to toggle approval"
                    >
                      {item.approved ? (
                        <>
                          <CheckCircle2 className="w-3 h-3" /> Approved
                        </>
                      ) : (
                        <>
                          <XCircle className="w-3 h-3" /> Unapproved
                        </>
                      )}
                    </button>
                  </div>

                  <p className="text-xs font-bold text-slate-800 line-clamp-2 leading-relaxed">
                    {item.caption || 'No caption provided.'}
                  </p>
                </div>

                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
                  <span className="truncate max-w-[160px]" title={item.attribution || item.source || ''}>
                    {item.attribution || item.source || 'Unknown'}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleOpenEdit(item)}
                      className="p-1.5 hover:bg-slate-100 text-slate-400 rounded-md transition-colors"
                      title="Edit Caption or URL"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-md transition-colors"
                      title="Delete Visual"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      <AnimatePresence>
        {editItem && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-3xl p-6 max-w-lg w-full shadow-2xl border border-slate-200 relative space-y-4"
            >
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <h3 className="text-lg font-black text-white flex items-center gap-2">
                  <Edit3 className="w-5 h-5 text-blue-600" /> Edit Lesson Visual
                </h3>
                <button onClick={() => setEditItem(null)} className="p-1 text-slate-400 hover:text-slate-400 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-black uppercase text-slate-400 block mb-1">Class / Subject</label>
                  <p className="text-sm font-bold text-white">{editItem.class_name} (Unit {editItem.lesson_unit}, Lesson {editItem.lesson_number})</p>
                </div>

                <div>
                  <label className="text-xs font-black uppercase text-slate-400 block mb-1">Media URL</label>
                  <Input
                    value={editUrl}
                    onChange={e => setEditUrl(e.target.value)}
                    placeholder="https://..."
                    className="text-xs"
                  />
                </div>

                <div>
                  <label className="text-xs font-black uppercase text-slate-400 block mb-1">Student Educational Caption</label>
                  <textarea
                    value={editCaption}
                    onChange={e => setEditCaption(e.target.value)}
                    rows={3}
                    className="w-full p-2.5 text-xs rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe how this image illustrates the lesson concept..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <Button variant="outline" onClick={() => setEditItem(null)} disabled={isSaving}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} disabled={isSaving} className="bg-blue-600 hover:bg-blue-700 text-white">
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Full Preview Modal */}
      <AnimatePresence>
        {previewItem && (
          <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={() => setPreviewItem(null)}>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 rounded-3xl overflow-hidden max-w-3xl w-full border border-white/10 shadow-2xl relative"
              onClick={e => e.stopPropagation()}
            >
              <div className="relative aspect-video bg-black flex items-center justify-center">
                {previewItem.media_type === 'image' ? (
                  <img
                    src={previewItem.url}
                    alt={previewItem.caption || ''}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <iframe
                    src={previewItem.url}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
                <button
                  onClick={() => setPreviewItem(null)}
                  className="absolute top-4 right-4 p-2 bg-black/60 rounded-full text-white hover:bg-black"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-2 text-white">
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 rounded-full text-xs font-black bg-blue-500/20 text-blue-400 border border-blue-500/30">
                    {previewItem.class_name}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-white/10 text-white/80">
                    Unit {previewItem.lesson_unit} · Lesson {previewItem.lesson_number}
                  </span>
                </div>
                <h4 className="text-base font-bold text-white/90">{previewItem.caption}</h4>
                <p className="text-xs text-white/50">{previewItem.attribution || previewItem.source}</p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add Visual Modal */}
      <AnimatePresence>
        {isAddOpen && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-3xl p-6 max-w-lg w-full shadow-2xl border border-slate-200 relative space-y-4 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <h3 className="text-lg font-black text-white flex items-center gap-2">
                  <Plus className="w-5 h-5 text-blue-600" /> Add Custom Lesson Visual
                </h3>
                <button onClick={() => setIsAddOpen(false)} className="p-1 text-slate-400 hover:text-slate-400 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCreateMedia} className="space-y-4">
                <div>
                  <label className="text-xs font-black uppercase text-slate-400 block mb-1">Target Class / Course</label>
                  <select
                    value={newClassId}
                    onChange={e => setNewClassId(e.target.value)}
                    className="w-full p-2.5 text-xs rounded-xl border border-slate-200 bg-slate-50 font-bold text-slate-800"
                  >
                    <option value="">-- General / No Specific Class --</option>
                    {classes.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-black uppercase text-slate-400 block mb-1">Unit Number</label>
                    <Input
                      type="number"
                      min={1}
                      value={newUnit}
                      onChange={e => setNewUnit(Number(e.target.value))}
                      className="text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-black uppercase text-slate-400 block mb-1">Lesson Number</label>
                    <Input
                      type="number"
                      min={1}
                      value={newLesson}
                      onChange={e => setNewLesson(Number(e.target.value))}
                      className="text-xs font-bold"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-black uppercase text-slate-400 block mb-1">Media Type</label>
                    <select
                      value={newType}
                      onChange={(e: any) => setNewType(e.target.value)}
                      className="w-full p-2.5 text-xs rounded-xl border border-slate-200 bg-slate-50 font-bold"
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-black uppercase text-slate-400 block mb-1">Source</label>
                    <Input
                      value={newSource}
                      onChange={e => setNewSource(e.target.value)}
                      placeholder="custom / partner"
                      className="text-xs"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-black uppercase text-slate-400 block mb-1">Media URL *</label>
                  <Input
                    required
                    value={newUrl}
                    onChange={e => setNewUrl(e.target.value)}
                    placeholder="https://images.unsplash.com/... or https://..."
                    className="text-xs"
                  />
                </div>

                <div>
                  <label className="text-xs font-black uppercase text-slate-400 block mb-1">Student Educational Caption</label>
                  <textarea
                    value={newCaption}
                    onChange={e => setNewCaption(e.target.value)}
                    rows={2}
                    className="w-full p-2.5 text-xs rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Concise educational explanation of what this visual demonstrates..."
                  />
                </div>

                <div>
                  <label className="text-xs font-black uppercase text-slate-400 block mb-1">Attribution / Author</label>
                  <Input
                    value={newAttribution}
                    onChange={e => setNewAttribution(e.target.value)}
                    placeholder="e.g. Photo by NASA on Unsplash"
                    className="text-xs"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                  <Button type="button" variant="outline" onClick={() => setIsAddOpen(false)} disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSaving || !newUrl} className="bg-blue-600 hover:bg-blue-700 text-white">
                    {isSaving ? 'Adding...' : 'Add to Lesson Archive'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
